import React from 'react';
import { BookOpen, ShieldCheck, CreditCard, MapPin, AlertCircle, CheckCircle2, ChevronLeft } from 'lucide-react';
import { AppView } from '../types';

interface NRIGuideProps { setView: (view: AppView) => void; }

const NRIGuide: React.FC<NRIGuideProps> = ({ setView }) => {
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      <button onClick={() => setView(AppView.GUIDES)}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-indigo-600 transition mb-6">
        <ChevronLeft size={16} /> All Guides
      </button>
      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-700 to-indigo-500 rounded-2xl p-8 mb-8 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen size={32} />
          <span className="text-indigo-200 font-semibold text-sm uppercase tracking-wider">2026 NRI Relocation Blueprint</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-extrabold leading-tight mb-3">
          Demystifying OCI Passport Updates & Aadhaar Rules for Your Family
        </h1>
        <p className="text-indigo-100 text-base">
          Returning to India after years in the US, UK, Germany, or Singapore is an exciting milestone,
          but the bureaucratic maze can quickly become overwhelming.
        </p>
      </div>

      {/* Article Hero Image */}
      <div className="rounded-2xl overflow-hidden h-72 mb-8 shadow-lg relative">
        <img
          src="/images/nri-guide-hero.png"
          alt="NRI family arriving at Indian airport"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        <div className="absolute bottom-4 left-5">
          <span className="text-white/80 text-xs font-semibold uppercase tracking-widest">Your Journey Home Starts Here</span>
        </div>
      </div>

      {/* Intro */}
      <div className="prose max-w-none mb-8">
        <p className="text-slate-600 text-base leading-relaxed">
          For mixed-citizenship families—where parents might hold Indian passports (NRIs) while the children
          and spouse hold foreign passports and OCI cards—the rules are fragmented and often misunderstood.
          Misinformation online can lead to denied boarding at the airport, frozen bank accounts, and delayed
          school admissions. To protect your family's transition, you need an irrefutable, legally sound strategy.
          Here is the definitive guide to navigating the OCI and Aadhaar landscape without the headaches.
        </p>
      </div>

      {/* Part 1 */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-indigo-100 p-2 rounded-lg">
            <ShieldCheck size={22} className="text-indigo-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 1: The OCI Passport Synchronization Trap</h2>
        </div>
        <p className="text-slate-600 mb-4">
          Your Overseas Citizen of India (OCI) card is a lifelong visa, but it is not a "set and forget" document.
          It is intrinsically tied to your foreign passport. When that passport is renewed, the Indian government
          does not automatically know.
        </p>

        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-4 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-3">The "Age 20" Rule: Physical Re-issue vs. Online Update</h3>
          <p className="text-slate-600 mb-3 text-sm">
            The most common misconception is that you must pay for a new physical OCI booklet every time your passport
            expires. The actual legal framework is much simpler:
          </p>
          <div className="space-y-3">
            <div className="flex gap-3">
              <CheckCircle2 size={18} className="text-indigo-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-slate-700 text-sm">Mandatory Physical Re-issue</p>
                <p className="text-slate-500 text-sm">You only need a brand-new physical OCI card once in your lifetime: when your passport is renewed for the first time after your 20th birthday. This captures adult biometrics.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 size={18} className="text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-slate-700 text-sm">Gratis Online Update</p>
                <p className="text-slate-500 text-sm">For every other passport renewal (under age 20, or any renewal after that initial post-20 reissue), you simply complete a free online update on the OCI portal.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-4 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-3">The 90-Day Deadline & APIS Integration</h3>
          <p className="text-slate-600 text-sm mb-3">
            You are legally required to upload your new passport details to the OCI portal within <strong>90 days</strong> of receiving the new document.
          </p>
          <p className="text-slate-600 text-sm">
            India's Bureau of Immigration now uses the <strong>Advance Passenger Information System (APIS)</strong>. If the foreign passport number you use to book your flight does not match the passport number actively linked in the OCI database, the high-speed e-gates will reject you, leading to manual delays or even denied boarding.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-4 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-2">The "Silent Rejection" Reality</h3>
          <p className="text-slate-600 text-sm mb-3">
            Many applicants submit their digital update and assume they are clear, only to find out months later it was rejected. The OCI portal has incredibly strict, often unspoken rules for digital footprints. Your passport PDF, recent photograph, and digital signature must meet exact aspect ratios, DPI, and file size limits.
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-amber-800 text-sm">Expert Tip</p>
              <p className="text-amber-700 text-sm">
                Always carry a printed copy of your online update "Acknowledgement Slip" when traveling.
                If the APIS system lags at the port of entry, this paper is your irrefutable proof of legal compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Part 2 */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-indigo-100 p-2 rounded-lg">
            <CreditCard size={22} className="text-indigo-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 2: The Aadhaar Reality Check (NRI vs. OCI)</h2>
        </div>
        <p className="text-slate-600 mb-5">
          Aadhaar is the backbone of Indian residency—required for everything from unblocking financial accounts to securing
          local SIM cards. However, the eligibility rules drastically differ depending on the passport you hold.
        </p>

        <h3 className="font-bold text-slate-700 mb-3">The 182-Day Divide</h3>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-indigo-600 text-white">
                <th className="text-left p-4 font-semibold">Citizenship Status</th>
                <th className="text-left p-4 font-semibold">Aadhaar Eligibility Rule</th>
                <th className="text-left p-4 font-semibold">Strategic Action Plan</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800 align-top">Indian Passport (NRI)</td>
                <td className="p-4 text-slate-600 align-top">
                  <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded mb-1">Immediate Eligibility</span>
                  <p>The 182-day waiting period was abolished for Indian citizens.</p>
                </td>
                <td className="p-4 text-slate-600 align-top">Apply on Day 1 of your return at an Aadhaar Seva Kendra. Use this immediate access to set up utilities and link your PAN card.</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="p-4 font-semibold text-slate-800 align-top">Foreign Passport (OCI)</td>
                <td className="p-4 text-slate-600 align-top">
                  <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded mb-1">Must wait 182 days</span>
                  <p>You are legally classified as a "Resident Foreign National."</p>
                </td>
                <td className="p-4 text-slate-600 align-top">You must reside in India for 182 days in the preceding 12 months before applying. Retain all boarding passes and passport entry stamps.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-blue-800 text-sm">
            <strong>Note:</strong> If your OCI family members have made short exploratory trips to India in the 12 months prior to your final relocation, those days count toward the 182-day threshold.
          </p>
        </div>
      </section>

      {/* Part 3 */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-indigo-100 p-2 rounded-lg">
            <CheckCircle2 size={22} className="text-indigo-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 3: Aadhaar for OCI Children (The Biometric Timeline)</h2>
        </div>
        <p className="text-slate-600 mb-5">
          For families navigating childcare or elementary school admissions, timing your children's Aadhaar applications
          is critical to avoid redundant bureaucratic trips.
        </p>

        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-indigo-600 text-white">
                <th className="text-left p-4 font-semibold">Child's Age Bracket</th>
                <th className="text-left p-4 font-semibold">Biometric Rules</th>
                <th className="text-left p-4 font-semibold">Expert Advice</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800 align-top">Under 5 Years</td>
                <td className="p-4 text-slate-600 align-top">No biometrics taken. Receives a "Baal Aadhaar" linked to a parent.</td>
                <td className="p-4 text-slate-600 align-top">If the child is under 4.5 years old (and has met the 182-day OCI rule), apply normally.</td>
              </tr>
              <tr className="bg-slate-50 border-b border-slate-100">
                <td className="p-4 font-semibold text-slate-800 align-top">Approaching 5th Birthday</td>
                <td className="p-4 text-slate-600 align-top">Full biometrics (10 fingerprints, iris scan) become mandatory at age 5.</td>
                <td className="p-4 text-slate-600 align-top">
                  <span className="font-bold text-amber-700">Wait.</span> If the child is a few months away from turning 5, delay the application until after their birthday so you don't have to repeat the enrollment process twice in a single year.
                </td>
              </tr>
              <tr className="bg-white">
                <td className="p-4 font-semibold text-slate-800 align-top">5 to 14 Years</td>
                <td className="p-4 text-slate-600 align-top">Full biometrics required.</td>
                <td className="p-4 text-slate-600 align-top">Apply normally (subject to OCI wait). A mandatory biometric update will be required again at age 15.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Part 4 */}
      <section className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-indigo-100 p-2 rounded-lg">
            <MapPin size={22} className="text-indigo-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">Part 4: Establishing Address Proof Without the Bureaucracy</h2>
        </div>
        <p className="text-slate-600 mb-5">
          Many outdated guides claim you need a complex "Address Proof Form" signed by a Gazetted Officer to get an Aadhaar.
          For returning professionals, this is unnecessary and overly complicated. Standard legal documents are perfectly acceptable.
        </p>

        <h3 className="font-bold text-slate-700 mb-3">The Best Documents to Use:</h3>
        <div className="space-y-3">
          {[
            {
              title: 'Registered Lease Agreement',
              desc: 'If you are settling into a new city and have executed a registered lease agreement (for example, for a rental property in Pune), this is ironclad proof of address. Ensure it is formally registered with the state, not just notarized.'
            },
            {
              title: 'Property / Sale Deed',
              desc: 'If you are moving directly into a purchased home, a registered sale deed works perfectly.'
            },
            {
              title: 'Family Linkage',
              desc: 'If the lease or deed is only in one parent\'s name, the other spouse and the children can easily piggyback on it by presenting a marriage certificate and birth certificates to establish the relationship.'
            }
          ].map((item, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 flex gap-4 shadow-sm">
              <div className="bg-indigo-600 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">
                {i + 1}
              </div>
              <div>
                <p className="font-bold text-slate-800 mb-1">{item.title}</p>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Signature */}
      <div className="border-t border-slate-200 pt-6 mt-8 text-center">
        <p className="text-slate-400 text-xs uppercase tracking-widest font-semibold">Published by</p>
        <p className="text-indigo-700 font-extrabold text-lg tracking-wide mt-1">NRIGUIDE.BHARAT</p>
      </div>
    </div>
  );
};

export default NRIGuide;
